import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, exhaustMap, firstValueFrom, map, of, tap } from 'rxjs';
import { DynamicDocumentService } from 'src/app/shared/services/dynamic-document.service';
import { Product } from './product.repository';
import { DatePipe } from '@angular/common';

const COLLECTION_NAME = 'dynamic_comparator_entity';

export type Comparator = {
    obj: any;
};

@Injectable({ providedIn: 'root' })
export class ComparatorRepository {
    public comparatorCount$ = new BehaviorSubject<number>(0);

    currentComparator?: Comparator;

    constructor(
        private dynamicDocumentService: DynamicDocumentService,
        private datePipe: DatePipe
    ) {}

    getMyComparators(): Observable<Product[]> {
        return this.dynamicDocumentService
            .find(
                {
                    collectionName: COLLECTION_NAME,
                    filters: [
                        {
                            fieldName: 'linkToNaturalPersons',
                            conditions: [
                                {
                                    type: 'EQUALS',
                                    value: localStorage.getItem('surname'),
                                },
                            ],
                        },
                    ],
                    page: 1,
                    pageSize: 1,
                    sorts: {
                        updatingDate: 'DESC',
                    },
                },
                true,
                false
            )
            .pipe(
                map((res) => {
                    const userId = localStorage.getItem("userid");
                    return { ...res, content: res.content.filter((cont: any) => cont?.linkToNaturalPersons?.documentId === userId) ?? [] }
                }),
                /* tap((res: any) => {
                    this.currentComparator = this.convertComparator(res?.content?.[0]);
                }), */
                map((res: any) =>
                    res?.content?.[0]?.linkToProduct.map((doc: any) => doc.documentId)
                ),
                exhaustMap((documentIds: string[]) => {
                    if (documentIds?.length > 0) {
                        return this.dynamicDocumentService
                            .findByIds('dynamic_product', documentIds, true)
                            .pipe(
                                map((producs) =>
                                    producs.map((product: any) =>product)
                                )
                            );
                    } else {
                        return of([]);
                    }
                })
            );
    }

    createMyComparators(): Observable<void> {
        return this.dynamicDocumentService.saveDocument(COLLECTION_NAME, { linkToNaturalPersons: localStorage.getItem('userid') ?? null }, true).pipe(
            tap((res: any) => {
                this.currentComparator = this.convertComparator(res);
            })
        );
    }

    requestCountComparators(): Observable<void> {
        return this.dynamicDocumentService
            .find(
                {
                    collectionName: COLLECTION_NAME,
                    filters: [
                        {
                            fieldName: 'linkToNaturalPersons',
                            conditions: [
                                {
                                    type: 'EQUALS',
                                    value: localStorage.getItem('surname'),
                                },
                            ],
                        },
                    ],
                    page: 1,
                    pageSize: 1,
                    sorts: {
                        updatingDate: 'DESC',
                    },
                },
                true,
                false
            )
            .pipe(
                map((res) => {
                    const userId = localStorage.getItem("userid");
                    return { ...res, content: res.content.filter((cont: any) => cont?.linkToNaturalPersons?.documentId === userId) ?? [] }
                }),
                tap((res: any) => {
                    this.currentComparator = this.convertComparator(res?.content?.[0]);
                }),
                map((res) => {
                    this.comparatorCount$.next(res?.content?.[0]?.linkToProduct?.length);
                })
            );
    }

    deleteProduct(productIds: string[]) {
        if (
            !this.currentComparator ||
            productIds.length === 0 ||
            this.currentComparator?.obj?.linkToProduct?.length === 0
        ) {
            return;
        }
        this.currentComparator.obj.linkToProduct =
            this.currentComparator?.obj?.linkToProduct?.filter(
                (p: any) => !productIds.includes(p?.documentId)
            );
        this.currentComparator.obj.creationDate = this.datePipe.transform(
            this.currentComparator.obj.creationDate,
            "yyyy-MM-dd'T'HH:mm:ss"
        );
        this.currentComparator.obj.updatingDate = this.datePipe.transform(
            Date.now(),
            "yyyy-MM-dd'T'00:00:00"
        );
        this.dynamicDocumentService
            .saveDocument(COLLECTION_NAME, this.currentComparator?.obj, true)
            .subscribe((res) => {
                if (this.currentComparator) {
                    this.currentComparator = this.convertComparator(res);
                    this.comparatorCount$.next(this.currentComparator?.obj?.linkToProduct?.length);
                }
            });
    }

    async addProduct(productIds: string[]) {
        if (productIds.length === 0) {
            return;
        }

        if (!this.currentComparator) {
            await firstValueFrom(this.requestCountComparators());
        }

        if (!this.currentComparator || !this.currentComparator.obj) {
            await firstValueFrom(this.createMyComparators());
        }

        if (!this.currentComparator) {
            return;
        }

        if (
            this.currentComparator?.obj?.linkToProduct?.some((p: any) =>
                productIds.includes(p.documentId)
            )
        ) {
            return;
        }

        if (!this.currentComparator?.obj?.linkToProduct)
            this.currentComparator.obj.linkToProduct = [];

        this.currentComparator.obj.linkToProduct?.push(...productIds);

        this.currentComparator.obj.creationDate = this.datePipe.transform(
            this.currentComparator.obj.creationDate,
            "yyyy-MM-dd'T'HH:mm:ss"
        );
        this.currentComparator.obj.updatingDate = this.datePipe.transform(
            Date.now(),
            "yyyy-MM-dd'T'00:00:00"
        );
        this.dynamicDocumentService
            .saveDocument(COLLECTION_NAME, this.currentComparator?.obj, true)
            .subscribe((res) => {
                if (this.currentComparator) {
                    this.currentComparator = this.convertComparator(res);
                    this.comparatorCount$.next(this.currentComparator?.obj?.linkToProduct?.length);
                }
            });
    }

    //--------
    convertComparator(data: any): Comparator {
        return {
            obj: data,
        };
    }

    convertProduct(data: any): Product {
        return {
            id: data?.id,
            title: data?.subject?.fr_FR ?? data?.shortDescription?.fr_FR,
            image: data?.document?.[0]?.url,
            referenceProduct: data?.referenceProduct,
        };
    }
}
